import React, { useCallback, useRef, useState } from 'react';
import { Checkbox, Form, Input, Upload } from 'antd';
import { useEffect } from 'react';
import { Sport } from '../../../types/types';
import styled from 'styled-components';
import {
  CalendarOutlined,
  CloseOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Drawer } from '../../common/Drawer';
import Button from '../../common/Button';
import {
  FooterRow,
  FormSection,
  SectionTitle,
  StyledCol,
  StyledFormItem,
} from '../Facility/FacilityFormModal';
import { COLORS, TEXT_SIZES } from '../../../utils/constants';
import { SportsCreationWithImage } from '../../../pages/SportsManagement';
interface SportModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: SportsCreationWithImage) => Promise<void>;
  initialValues: Sport | null;
}

const SportsModal: React.FC<SportModalProps> = ({
  visible,
  onClose,
  onSubmit,
  initialValues,
}) => {
  const [form] = Form.useForm();
  const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(false);

  const [isTeamSport, setIsTeamSport] = useState<boolean>(
    initialValues?.isTeamSport ?? false,
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFormChange = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some(({ errors }) => errors.length > 0);

    const currentValues = form.getFieldsValue();

    let hasChanges = false;
    if (initialValues) {
      hasChanges = Object.keys(currentValues).some((key) => {
        const initialValue = initialValues[key as keyof unknown];
        const currentValue = currentValues[key as keyof unknown];
        return initialValue !== currentValue;
      });
    } else {
      hasChanges = Object.keys(currentValues).every(
        (key) =>
          [
            'isTeamSport',
            'sportMinPlayersPerTeam',
            'sportMaxPlayersPerTeam',
          ].includes(key) || form.isFieldTouched(key),
      );
    }
    setIsButtonEnabled(!hasErrors && hasChanges);
  }, [form, initialValues]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formattedValues: SportsCreationWithImage = {
        ...values,
        isTeamSport: values.isTeamSport ?? false,
      };

      await onSubmit(formattedValues);
    } catch (errorInfo) {
      console.warn('Validation failed:', errorInfo);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setIsTeamSport(false);
    setIsButtonEnabled(false);
    setPreviewUrl(null);
    onClose();
  };

  // Sync all form/preview state to the selected sport every time the drawer opens.
  // Driven by `visible` (not the image URL) so reopening the same sport restores its image,
  // and switching sports never retains the previous sport's image.
  useEffect(() => {
    if (!visible) return;

    form.resetFields();

    if (initialValues) {
      form.setFieldsValue(initialValues);
      setIsTeamSport(initialValues.isTeamSport ?? false);
      setPreviewUrl(initialValues.attachmentUrlDto?.attachmentUrl ?? null);
    } else {
      setIsTeamSport(false);
      setPreviewUrl(null);
    }
    setIsButtonEnabled(false);
  }, [visible, initialValues, form]);

  useEffect(() => {
    return () => {
      form.resetFields();
    };
  }, []);

  useEffect(() => {
    if (!isTeamSport) {
      form.setFieldsValue({
        sportMinPlayersPerTeam: null,
        sportMaxPlayersPerTeam: null,
      });
    }
  }, [isTeamSport]);

  return (
    <Drawer
      title={initialValues ? 'Edit Sport' : 'Add New Sport'}
      open={visible}
      onClose={handleClose}
      destroyOnHidden
      width={'40vw'}
      footer={[
        <FooterRow key="form-actions">
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit}
            disabled={!isButtonEnabled}
          >
            {initialValues ? 'Update' : 'Create'} Sport
          </Button>
        </FooterRow>,
      ]}
    >
      <StyledCol>
        <Form form={form} layout="vertical" onFieldsChange={handleFormChange}>
          <FormSection>
            <SectionTitle>
              <CalendarOutlined /> Basic Information
            </SectionTitle>

            <StyledFormItem
              name="sportName"
              label="Sports Name"
              rules={[{ required: true, message: 'Please enter sport name' }]}
            >
              <StyledInput placeholder="Enter sports name" size="large" />
            </StyledFormItem>
            <StyledFormItem
              name="sportDescription"
              label="Description"
              rules={[
                {
                  required: true,
                  message: 'Please enter description',
                },
              ]}
            >
              <StyledInput
                size="large"
                placeholder="Brief description of the sports and its features"
                maxLength={200}
                showCount
              />
            </StyledFormItem>
            <ImageContainer>
              {previewUrl && (
                <PreviewWrapper>
                  <ImagePreview
                    src={previewUrl || ''}
                    alt="Uploaded sport image"
                  />
                  <StyledCloseOutlined
                    onClick={() => {
                      setPreviewUrl(null);
                      form.setFieldValue('uploadSportImage', undefined);
                      form.validateFields(['uploadSportImage']);
                      form.setFields([
                        { name: 'uploadSportImage', touched: true },
                      ]);
                    }}
                  />
                </PreviewWrapper>
              )}
              <HiddenUploadWrapper $hidden={!!previewUrl}>
                <StyledUploadOutlined />
                <NoMarginFormItem
                  name="uploadSportImage"
                  label="Upload Sport Image"
                  valuePropName="fileList"
                  required
                  getValueFromEvent={(e) => {
                    const fileList = e?.fileList;
                    if (!fileList || fileList.length === 0) return fileList;

                    const file = fileList[0]?.originFileObj;
                    if (file) {
                      const allowedTypes = [
                        'image/jpeg',
                        'image/png',
                        'image/jpg',
                      ];
                      const allowedExtensions = ['jpg', 'jpeg', 'png'];
                      const fileExtension = file.name
                        .split('.')
                        .pop()
                        ?.toLowerCase();

                      const isValidExtension =
                        allowedExtensions.includes(fileExtension);
                      const isValidType = allowedTypes.includes(file.type);
                      //please do not remove : might re-use in future
                      // const isValidSize = file.size / 1024 / 1024 <= 5;

                      if (isValidType && isValidExtension) {
                        const preview = URL.createObjectURL(file);
                        setPreviewUrl(preview);
                      } else {
                        setPreviewUrl(null);
                      }
                    }
                    return fileList;
                  }}
                  rules={[
                    {
                      validator: (_, fileList) => {
                        const hasExistingImage =
                          !!initialValues?.attachmentUrlDto?.attachmentId;
                        const hasPreview = !!previewUrl;
                        const hasNewUpload = fileList && fileList.length > 0;
                        if (
                          (!hasExistingImage && !hasNewUpload) ||
                          (hasExistingImage && !hasPreview && !hasNewUpload)
                        ) {
                          return Promise.reject('Please upload a sport image');
                        }
                        if (hasNewUpload) {
                          const file = fileList[0].originFileObj;
                          const allowedTypes = [
                            'image/jpeg',
                            'image/png',
                            'image/jpg',
                          ];
                          const allowedExtensions = ['jpg', 'jpeg', 'png'];
                          const fileExtension = file.name
                            .split('.')
                            .pop()
                            ?.toLowerCase();

                          if (
                            !allowedTypes.includes(file.type) ||
                            !allowedExtensions.includes(fileExtension)
                          ) {
                            return Promise.reject(
                              'Only JPG, PNG, or JPEG files are allowed',
                            );
                          }
                          //please do not remove : might re-use in future
                          // if (file.size / 1024 / 1024 > 5) {
                          //   return Promise.reject(
                          //     'Image must be smaller than 5MB',
                          //   );
                          // }
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Upload
                    maxCount={1}
                    beforeUpload={() => false}
                    showUploadList={false}
                  >
                    <Button>Choose File</Button>
                  </Upload>
                </NoMarginFormItem>
              </HiddenUploadWrapper>
              <SupportedFormatsText>
                Supported formats: JPG, PNG, JPEG
              </SupportedFormatsText>
            </ImageContainer>
            <StyledFormItem
              name="sportCode"
              label="Sports Code"
              rules={[
                { required: true, message: 'Please enter sport code' },
                { max: 3, message: 'Code cannot be bigger than 3 letters' },
              ]}
            >
              <StyledInput placeholder="Enter sports name" size="large" />
            </StyledFormItem>
            <StyledFormItem
              name="isTeamSport"
              label="Team Sport"
              valuePropName="checked"
            >
              <Checkbox
                onChange={(event) => setIsTeamSport(event.target.checked)}
              />
            </StyledFormItem>
            <StyledFormItem
              name="sportMinPlayersPerTeam"
              label="Min Players Per Team"
              rules={[
                {
                  required: isTeamSport,
                  message: 'Min Players Per Team is required',
                },
                {
                  validator: async (_, value) => {
                    if (!isTeamSport) return Promise.resolve();

                    const numValue = parseFloat(value);
                    if (Number.isNaN(numValue)) {
                      return Promise.reject('Value must be a valid number');
                    }
                    if (numValue < 0) {
                      return Promise.reject('Min cannot be less than 0');
                    }

                    const maxValue = form.getFieldValue(
                      'sportMaxPlayersPerTeam',
                    );
                    if (value && maxValue && Number(value) > Number(maxValue)) {
                      return Promise.reject('Min cannot be greater than Max');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <StyledInput
                placeholder="Min Players Per Team"
                size="large"
                type="number"
                disabled={!isTeamSport}
              />
            </StyledFormItem>
            <StyledFormItem
              name="sportMaxPlayersPerTeam"
              label="Max Players Per Team"
              rules={[
                {
                  required: isTeamSport,
                  message: 'Max Players Per Team is required',
                },
                {
                  validator: async (_, value) => {
                    if (!isTeamSport) return Promise.resolve();

                    const numValue = parseFloat(value);
                    if (Number.isNaN(numValue)) {
                      return Promise.reject('Value must be a valid number');
                    }
                    if (numValue < 0) {
                      return Promise.reject('Max cannot be less than 0');
                    }

                    const minValue = form.getFieldValue(
                      'sportMinPlayersPerTeam',
                    );
                    if (value && minValue && Number(value) < Number(minValue)) {
                      return Promise.reject('Max cannot be less than Min');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <StyledInput
                placeholder="Max Players Per Team"
                size="large"
                type="number"
                disabled={!isTeamSport}
              />
            </StyledFormItem>
          </FormSection>
        </Form>
      </StyledCol>
    </Drawer>
  );
};

const StyledInput = styled(Input)`
  height: 43px;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  border: 2px ${COLORS.grey.secondaryLight} dashed;
  border-radius: 6px;
  gap: 8px;
  margin-bottom: 18px;
  padding: 18px 0px;
`;

const SupportedFormatsText = styled.p`
  margin: 0px;
  font-size: ${TEXT_SIZES.XS}px;
`;

const NoMarginFormItem = styled(Form.Item)`
  margin-bottom: 0 !important;
  flex: 1;

  & .ant-form-item-label {
    width: 100%;
    text-align: center !important;
  }

  .ant-form-item-control-input-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const StyledUploadOutlined = styled(UploadOutlined)`
  color: ${COLORS.black.primary};
  font-size: ${TEXT_SIZES.XL};
`;

const ImagePreview = styled.img`
  width: 150px;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid ${COLORS.grey.secondaryLight};
  margin-bottom: 12px;
  flex-shrink: 0;
`;

const PreviewWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const StyledCloseOutlined = styled(CloseOutlined)`
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: ${TEXT_SIZES.XS}px;
  color: ${COLORS.white.primary};
  background: ${COLORS.black.background};
  border-radius: 50%;
  padding: 4px;
  cursor: pointer;
`;

const HiddenUploadWrapper = styled.div<{ $hidden: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  flex-direction: column;
  align-items: center;
`;

export default SportsModal;
